/**
 * PreRender Muxing Exporter
 *
 * STRATÉGIE : Pré-rendre SEULEMENT les parties avec effets, stream copy le reste
 *
 * PRINCIPE :
 * 1. Pour chaque clip vidéo, identifier les zones où des effets sont actifs
 * 2. Découper le clip en sous-segments :
 *    - Segments SANS effets → stream copy (ultra rapide)
 *    - Segments AVEC effets → pré-rendu (WebCodecs)
 * 3. Muxer TOUT avec FFmpeg concat
 *
 * EXEMPLE :
 * Clip vidéo: [=============================] (60s)
 * Intro:      [===]                           (5s)
 * Outro:                              [===]   (5s)
 *
 * → Segment 1: [===]                    → PRÉ-RENDU (5s avec intro)
 * → Segment 2:      [================]  → STREAM COPY (50s)
 * → Segment 3:                    [===] → PRÉ-RENDU (5s avec outro)
 *
 * AVANTAGES :
 * - Ultra rapide pour les parties SANS effets (stream copy)
 * - Effets supportés (zoom, split, text, intro/outro)
 * - Rendu minimal : seules les parties nécessaires sont ré-encodées
 */

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';
import { Clip, Track, SubtitleStyle } from './types';
import { ExportConfig, ProgressCallback, ExportProgress, ASPECT_RATIOS } from './webcodecs-exporter';
import { FrameRenderer } from './frame-renderer';
import { FFmpegOutOfMemoryError, isLikelyFFmpegOutOfMemoryError } from './ffmpeg-errors';
import { FFmpegService } from './ffmpeg-service';
import { getClipExportSourceUrl } from './utils/clip-helpers';

export type PreRenderMuxingExportConfig = ExportConfig;

type OutputFormat = 'webm' | 'mp4';

// Intervalle de temps avec ou sans effet
interface TimeInterval {
  start: number;      // Temps de début dans le clip (relatif au trimStart)
  end: number;        // Temps de fin dans le clip
  hasEffect: boolean; // Si true, nécessite pré-rendu
  effects: {
    zoom: Clip | null;
    split: Clip | null;
    texts: Clip[];
    images: Clip[];
    synthes: Clip[];
    panels: Clip[];
    intro: Clip | null;
    outro: Clip | null;
    imageMask: string | null; // activeImageMask URL
  };
}

// Segment à exporter (pré-rendu ou stream copy)
interface ExportSegment {
  type: 'prerender' | 'streamcopy';
  sourceClip: Clip;
  startInSource: number;  // Position de début dans la source vidéo
  duration: number;
  timelineStart: number;  // Position sur la timeline (pour les overlays)
  effects: TimeInterval['effects'];
  outputFilename?: string;
}

export class PreRenderMuxingExporter {
  private config: PreRenderMuxingExportConfig;
  private progressCallback: ProgressCallback;
  private ffmpeg: FFmpeg | null = null;
  private isFFmpegLoaded = false;
  private aborted = false;
  private sourceCache = new Map<string, string>();
  private sourceSequence = 0;
  private ffmpegLogs: string[] = [];

  private tempFileSequence = 0;
  private segmentDetails: any[] = [];


  private currentSegmentTargetDuration: number | null = null;
  private currentSegmentIndex: number | null = null;
  private lastProgressUpdate = 0;

  /**
   * Calculer la progression globale de l'export
   * @param segmentProgress Progression du segment actuel (0-100)
   */
  private calculateGlobalProgress(segmentProgress: number): number {
    if (this.currentSegmentIndex === null) return 0;

    // Structure du temps :
    // 10% : Préparation / Analyse
    // 60% : Rendu des segments (prerender + streamcopy)
    // 30% : FFmpeg Muxing final (concat + conversion)

    const segmentBase = 10;
    const segmentsWeights = 60;

    // Progression au sein des segments
    const segmentIndexWeight = (this.currentSegmentIndex / (this.segmentDetails.length || 1));
    const segmentIndividualWeight = (segmentProgress / 100) * (1 / (this.segmentDetails.length || 1));

    const totalSegmentsProgress = (segmentIndexWeight + segmentIndividualWeight) * segmentsWeights;

    return Math.min(99, Math.round(segmentBase + totalSegmentsProgress));
  }

  constructor(config: PreRenderMuxingExportConfig, progressCallback: ProgressCallback) {
    this.config = config;
    this.progressCallback = progressCallback;
  }

  abort(): void {
    this.aborted = true;
  }

  /**
   * Détecter le format de la vidéo source principale
   */
  private detectSourceFormat(clips: Clip[]): OutputFormat {
    const mainVideoClip = clips.find(c =>
      c.type === 'video' &&
      c.url &&
      c.clipRole !== 'intro' &&
      c.clipRole !== 'outro'
    );

    if (mainVideoClip?.url) {
      const url = mainVideoClip.url.toLowerCase();
      if (url.includes('.mp4') || url.includes('video/mp4')) {
        return 'mp4';
      }
    }

    return 'webm';
  }

  /**
   * Main export function
   */
  async export(
    clips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<Blob> {
    try {
      this.updateProgress({
        phase: 'preparing',
        progress: 0,
        message: 'Analyse des clips pour optimisation...'
      });

      // Charger FFmpeg
      await this.loadFFmpeg();

      // Étape 1: Créer les segments à exporter
      const segments = await this.createExportSegments(clips, tracks, activeSubtitleLanguage);

      const prerenderSegments = segments.filter(s => s.type === 'prerender');
      const streamCopySegments = segments.filter(s => s.type === 'streamcopy');

      console.log('[PreRenderMuxing] Segments:', {
        total: segments.length,
        prerender: prerenderSegments.length,
        streamCopy: streamCopySegments.length
      });

      // Helper pour l'alignement précis des frames
      const fps = this.config.fps || 30;
      const frameDuration = 1 / fps;

      // On aligne les durées pour éviter les micro-gaps
      segments.forEach(s => {
        s.duration = Math.round(s.duration / frameDuration) * frameDuration;
        s.startInSource = Math.round(s.startInSource / frameDuration) * frameDuration;
      });

      // Calculer les durées
      const prerenderDuration = prerenderSegments.reduce((sum, s) => sum + s.duration, 0);
      const streamCopyDuration = streamCopySegments.reduce((sum, s) => sum + s.duration, 0);

      const segmentDetails = segments.map((s, i) => {
        const effectsList = [];
        if (s.effects.intro) effectsList.push('Intro');
        if (s.effects.outro) effectsList.push('Outro');
        if (s.effects.zoom) effectsList.push('Zoom');
        if (s.effects.texts.length > 0) effectsList.push('Texte');
        if (s.effects.images.length > 0) effectsList.push('Image');
        if (s.effects.synthes.length > 0) effectsList.push('Synthé');
        if (s.effects.panels.length > 0) effectsList.push('Panel');
        if (s.effects.split) effectsList.push('Split');

        return {
          id: i,
          type: s.type, // 'prerender' | 'streamcopy'
          duration: s.duration,
          effects: effectsList,
          progress: 0 // Progress individuel (0-100)
        };
      });
      this.segmentDetails = segmentDetails;


      this.updateProgress({
        phase: 'preparing',
        progress: 10,
        message: `${prerenderSegments.length} segments à rendre (${Math.round(prerenderDuration)}s), ${streamCopySegments.length} en stream copy (${Math.round(streamCopyDuration)}s)`,
        details: this.segmentDetails // Envoi initial explicite
      });

      // Étape 2: Traiter chaque segment
      for (let i = 0; i < segments.length; i++) {
        if (this.aborted) throw new Error('Export annulé');

        const segment = segments[i];
        this.currentSegmentIndex = i;
        this.currentSegmentTargetDuration = segment.duration;
        const progress = this.calculateGlobalProgress(0);

        if (segment.type === 'prerender') {
          this.updateProgress({
            phase: 'encoding',
            progress,
            message: `Pré-rendu segment ${i + 1}/${segments.length} (${Math.round(segment.duration)}s avec effets)...`
          });

          segment.outputFilename = await this.preRenderSegment(segment, clips, tracks, activeSubtitleLanguage, subtitleStyle);
        } else {
          // OPTIMISATION MAJEURE : On ne fait RIEN pour les segments stream copy
          // On va juste les référencer dans le concat manifest avec inpoint/outpoint
          // On s'assure juste que le fichier source est disponible pour FFmpeg
          if (!getClipExportSourceUrl(segment.sourceClip)) throw new Error("Clip source sans URL");

          await this.ensureSourceFile(segment.sourceClip);

          // Debug
          console.log(`[PreRenderMuxing] Segment source (virtual) ${i + 1}/${segments.length}: ${segment.sourceClip.name} (${segment.duration.toFixed(1)}s)`);
          this.updateProgress({
            phase: 'preparing',
            progress,
            message: `Segment ${i + 1}/${segments.length} (source originale)...`
          });
        }
      }

      // Étape 3: Muxer tout
      this.updateProgress({
        phase: 'muxing',
        progress: 75,
        message: 'Assemblage final (Concat directives)...'
      });

      const finalBlob = await this.muxAllSegments(segments, clips, activeSubtitleLanguage);

      this.updateProgress({
        phase: 'complete',
        progress: 100,
        message: 'Export terminé !'
      });

      return finalBlob;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      this.updateProgress({
        phase: 'error',
        progress: 0,
        message: `Erreur: ${errorMessage}`
      });
      throw error;
    } finally {
      await this.cleanup();
    }
  }

  /**
   * Vérifier si les dimensions d'une vidéo correspondent exactement à la cible
   */
  private async checkVideoDimensions(url: string, targetWidth: number, targetHeight: number): Promise<boolean> {
    try {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.preload = 'metadata';

      await new Promise<void>((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = () => reject();
        video.src = url;
      });

      // Marge d'erreur de 1 pixel pour être flexible
      const widthMatch = Math.abs(video.videoWidth - targetWidth) <= 1;
      const heightMatch = Math.abs(video.videoHeight - targetHeight) <= 1;

      return widthMatch && heightMatch;
    } catch (e) {
      console.warn('[PreRenderMuxing] Erreur vérification dimensions:', e);
      return false;
    }
  }

  /**
   * Créer les segments d'export en découpant les clips selon les zones d'effet
   */
  private async createExportSegments(clips: Clip[], tracks: Track[], activeSubtitleLanguage: string): Promise<ExportSegment[]> {
    const segments: ExportSegment[] = [];

    // Filtrer les clips visibles
    const visibleClips = clips.filter(clip => {
      const track = tracks.find(t => t.id === clip.trackId);
      return track?.isVisible;
    });

    // Séparer par type
    const videoClips = visibleClips.filter(c =>
      c.type === 'video' &&
      c.url
    ).sort((a, b) => {
      if (a.start !== b.start) return a.start - b.start;
      // En cas d'égalité de temps, priorité à la piste (v1 avant v2)
      return (a.trackId || '') < (b.trackId || '') ? -1 : 1;
    });

    const zoomClips = visibleClips.filter(c => c.type === 'zoom');
    const textClips = visibleClips.filter(c => c.type === 'text');
    const imageClips = visibleClips.filter(c => c.type === 'image');
    const syntheClips = visibleClips.filter(c => c.type === 'synthe');
    const panelClips = visibleClips.filter(c => c.type === 'panel');
    const splitClips = visibleClips.filter(c => c.trackId === 's1' && c.type === 'video');
    const introClip = visibleClips.find(c => c.clipRole === 'intro');
    const outroClip = visibleClips.find(c => c.clipRole === 'outro');

    // Pour chaque clip vidéo, découper en segments
    for (const videoClip of videoClips) {
      const clipStart = videoClip.start;
      const clipEnd = videoClip.start + videoClip.duration;
      const trimStart = videoClip.trimStart || 0;
      const resolution = ASPECT_RATIOS[this.config.aspectRatio];

      // Vérifier si les dimensions correspondent à la cible
      const isDimensionsMatch = await this.checkVideoDimensions(videoClip.url, resolution.width, resolution.height);
      console.log(`[PreRenderMuxing] Clip ${videoClip.name}: dimensions ${isDimensionsMatch ? 'OK' : 'MISMATCH (re-render forced)'}`);

      // Collecter tous les intervalles d'effet qui touchent ce clip
      const effectIntervals: { start: number; end: number; type: string; clip: Clip }[] = [];

      // Helper pour vérifier le chevauchement
      const getOverlap = (effectClip: Clip): { start: number; end: number } | null => {
        const effectStart = effectClip.start;
        const effectEnd = effectClip.start + effectClip.duration;

        if (effectStart >= clipEnd || effectEnd <= clipStart) return null;

        return {
          start: Math.max(effectStart, clipStart),
          end: Math.min(effectEnd, clipEnd)
        };
      };

      // Ajouter les intervalles d'effet
      for (const zoom of zoomClips) {
        const overlap = getOverlap(zoom);
        if (overlap) effectIntervals.push({ ...overlap, type: 'zoom', clip: zoom });
      }
      for (const text of textClips) {
        const overlap = getOverlap(text);
        if (overlap) effectIntervals.push({ ...overlap, type: 'text', clip: text });
      }
      for (const image of imageClips) {
        const overlap = getOverlap(image);
        if (overlap) effectIntervals.push({ ...overlap, type: 'image', clip: image });
      }
      for (const synthe of syntheClips) {
        const overlap = getOverlap(synthe);
        if (overlap) effectIntervals.push({ ...overlap, type: 'synthe', clip: synthe });
      }
      for (const panel of panelClips) {
        const overlap = getOverlap(panel);
        if (overlap) effectIntervals.push({ ...overlap, type: 'panel', clip: panel });
      }
      for (const split of splitClips) {
        const overlap = getOverlap(split);
        if (overlap) effectIntervals.push({ ...overlap, type: 'split', clip: split });
      }
      if (introClip) {
        const overlap = getOverlap(introClip);
        if (overlap) effectIntervals.push({ ...overlap, type: 'intro', clip: introClip });
      }
      if (outroClip) {
        const overlap = getOverlap(outroClip);
        if (overlap) effectIntervals.push({ ...overlap, type: 'outro', clip: outroClip });
      }

      // Check subtitles (fixes burn-in issue) - ONLY IF burnSubtitles is enabled
      if (this.config.burnSubtitles && videoClip.subtitles && videoClip.subtitles[activeSubtitleLanguage]) {
        for (const sub of videoClip.subtitles[activeSubtitleLanguage]) {
          const subStart = sub.start;
          const subEnd = sub.end;
          if (subStart < clipEnd && subEnd > clipStart) {
            effectIntervals.push({
              start: Math.max(subStart, clipStart),
              end: Math.min(subEnd, clipEnd),
              type: 'subtitle',
              clip: videoClip
            });
          }
        }
      }

      // Si pas d'effets, vitesse normale (1x) ET dimensions compatibles, tout le clip est stream copy
      // Sinon, on force le pré-rendu (vitesse, effets ou crop)
      const hasSpeedChange = (videoClip.playbackRate || 1) !== 1;

      if (effectIntervals.length === 0 && !hasSpeedChange && isDimensionsMatch) {
        segments.push({
          type: 'streamcopy',
          sourceClip: videoClip,
          startInSource: trimStart,
          duration: videoClip.duration,
          timelineStart: clipStart,
          effects: { zoom: null, split: null, texts: [], images: [], synthes: [], panels: [], intro: null, outro: null, imageMask: null }
        });
        continue;
      }

      // Fusionner les intervalles qui se chevauchent pour créer des zones d'effet
      const mergedIntervals = this.mergeIntervals(effectIntervals.map(e => ({ start: e.start, end: e.end })));

      // Créer les segments basés sur les intervalles
      let currentPos = clipStart;

      for (const interval of mergedIntervals) {
        // Segment stream copy/prerender AVANT l'intervalle d'effet (s'il y a un gap)
        if (interval.start > currentPos && !hasSpeedChange && isDimensionsMatch) {
          const gapDuration = interval.start - currentPos;
          segments.push({
            type: 'streamcopy',
            sourceClip: videoClip,
            startInSource: trimStart + (currentPos - clipStart),
            duration: gapDuration,
            timelineStart: currentPos,
            effects: { zoom: null, split: null, texts: [], images: [], synthes: [], panels: [], intro: null, outro: null, imageMask: null }
          });
        } else if (interval.start > currentPos) {
          // Si vitesse modifiée OU dimensions mismatch, on force le pré-rendu
          const gapDuration = interval.start - currentPos;
          segments.push({
            type: 'prerender', // FORCE PRERENDER
            sourceClip: videoClip,
            startInSource: trimStart + (currentPos - clipStart) * (videoClip.playbackRate || 1),
            duration: gapDuration,
            timelineStart: currentPos,
            effects: { zoom: null, split: null, texts: [], images: [], synthes: [], panels: [], intro: null, outro: null, imageMask: null }
          });
        }

        // Segment pré-rendu pour l'intervalle d'effet
        const effectDuration = interval.end - interval.start;

        // Collecter les effets actifs pendant cet intervalle
        const activeEffects = {
          zoom: effectIntervals.find(e => e.type === 'zoom' && e.start < interval.end && e.end > interval.start)?.clip || null,
          split: effectIntervals.find(e => e.type === 'split' && e.start < interval.end && e.end > interval.start)?.clip || null,
          texts: effectIntervals.filter(e => e.type === 'text' && e.start < interval.end && e.end > interval.start).map(e => e.clip),
          images: effectIntervals.filter(e => e.type === 'image' && e.start < interval.end && e.end > interval.start).map(e => e.clip),
          synthes: effectIntervals.filter(e => e.type === 'synthe' && e.start < interval.end && e.end > interval.start).map(e => e.clip),
          panels: effectIntervals.filter(e => e.type === 'panel' && e.start < interval.end && e.end > interval.start).map(e => e.clip),
          intro: effectIntervals.find(e => e.type === 'intro' && e.start < interval.end && e.end > interval.start)?.clip || null,
          outro: effectIntervals.find(e => e.type === 'outro' && e.start < interval.end && e.end > interval.start)?.clip || null,
          imageMask: videoClip.activeImageMask || null,
        };

        // --- OPTIMISATION INTRO/OUTRO ---
        // Si c'est un effet Intro ou Outro SEUL (sans texte, image, zoom... ET s-titres),
        // ET que la vidéo est en mp4 avec les bonnes dimensions,
        // ALORS on fait un stream copy de l'intro/outro au lieu de pré-rendre.
        let isOptimized = false;
        const resolution = ASPECT_RATIOS[this.config.aspectRatio];

        // Vérifier s'il y a des sous-titres actifs sur cet intervalle
        const hasActiveSubtitle = effectIntervals.some(e => e.type === 'subtitle' && e.start < interval.end && e.end > interval.start);

        if (
          !activeEffects.zoom &&
          !activeEffects.split &&
          activeEffects.texts.length === 0 &&
          activeEffects.images.length === 0 &&
          activeEffects.synthes.length === 0 &&
          activeEffects.panels.length === 0 &&
          !hasActiveSubtitle
        ) {
          const introOrOutro = activeEffects.intro || activeEffects.outro;
          if (introOrOutro && introOrOutro.url && (introOrOutro.url.toLowerCase().includes('.mp4') || introOrOutro.url.toLowerCase().includes('video/mp4'))) {
            // Vérifier les dimensions
            const isDimensionsMatch = await this.checkVideoDimensions(introOrOutro.url, resolution.width, resolution.height);

            if (isDimensionsMatch) {
              console.log(`[PreRenderMuxing] Optimisation: Stream Copy pour ${activeEffects.intro ? 'Intro' : 'Outro'}`);
              segments.push({
                type: 'streamcopy',
                sourceClip: introOrOutro,
                // Pour l'intro/outro, on assume qu'on prend la partie correspondante au temps ecoulé
                // interval.start est le temps global timeline.
                // introOrOutro.start est le début de l'intro sur la timeline.
                startInSource: (introOrOutro.trimStart || 0) + (interval.start - introOrOutro.start),
                duration: effectDuration,
                timelineStart: interval.start,
                effects: activeEffects // On garde les effets pour info, mais type=streamcopy ignorera le rendu
              });
              isOptimized = true;
            }
          }
        }

        if (!isOptimized) {
          segments.push({
            type: 'prerender',
            sourceClip: videoClip,
            startInSource: trimStart + (interval.start - clipStart),
            duration: effectDuration,
            timelineStart: interval.start,
            effects: activeEffects
          });
        }

        currentPos = interval.end;
      }

      // Segment stream copy/prerender APRÈS le dernier intervalle d'effet (s'il reste du clip)
      if (currentPos < clipEnd) {
        const remainingDuration = clipEnd - currentPos;

        if (!hasSpeedChange && isDimensionsMatch) {
          segments.push({
            type: 'streamcopy',
            sourceClip: videoClip,
            startInSource: trimStart + (currentPos - clipStart),
            duration: remainingDuration,
            timelineStart: currentPos,
            effects: { zoom: null, split: null, texts: [], images: [], synthes: [], panels: [], intro: null, outro: null, imageMask: null }
          });
        } else {
          // FORCE PRERENDER for speed change or dimensions mismatch
          segments.push({
            type: 'prerender',
            sourceClip: videoClip,
            startInSource: trimStart + (currentPos - clipStart) * (videoClip.playbackRate || 1),
            duration: remainingDuration,
            timelineStart: currentPos,
            effects: { zoom: null, split: null, texts: [], images: [], synthes: [], panels: [], intro: null, outro: null, imageMask: null }
          });
        }
      }
    }

    // --- ÉTAPE FINALE: DÉDOUBLONNAGE ET ASSEMBLAGE ---
    // On s'assure que les segments ne se chevauchent pas dans la timeline finale.
    // Si deux clips se chevauchent, le premier dans videoClips (prioritaire) gagne pour la zone commune.
    const stitchedSegments: ExportSegment[] = [];
    let timelinePointer = 0;

    // Trier TOUS les segments par temps timeline
    segments.sort((a, b) => a.timelineStart - b.timelineStart);

    for (const seg of segments) {
      if (seg.timelineStart >= timelinePointer) {
        // Cas normal: segment après le pointeur (avec ou sans gap)
        stitchedSegments.push(seg);
        timelinePointer = seg.timelineStart + seg.duration;
      } else if (seg.timelineStart + seg.duration > timelinePointer) {
        // Chevauchement partiel: on rogne le début pour qu'il commence au pointeur
        const overlap = timelinePointer - seg.timelineStart;
        const previousDuration = seg.duration;

        seg.duration -= overlap;
        seg.startInSource += overlap * (seg.sourceClip.playbackRate || 1);
        seg.timelineStart = timelinePointer;

        if (seg.duration > 0.001) { // Ignorer les segments résiduels minuscules
          stitchedSegments.push(seg);
          timelinePointer = seg.timelineStart + seg.duration;
        }

        console.log(`[PreRenderMuxing] Segment ajusté (chevauchement): Durée ${previousDuration.toFixed(3)}s -> ${seg.duration.toFixed(3)}s`);
      }
      // Si seg.timelineStart + seg.duration <= timelinePointer, le segment est déjà couvert, on l'ignore.
    }

    return stitchedSegments;
  }

  /**
   * Fusionner les intervalles qui se chevauchent
   */
  private mergeIntervals(intervals: { start: number; end: number }[]): { start: number; end: number }[] {
    if (intervals.length === 0) return [];

    // Trier par début
    const sorted = [...intervals].sort((a, b) => a.start - b.start);
    const merged: { start: number; end: number }[] = [sorted[0]];

    for (let i = 1; i < sorted.length; i++) {
      const current = sorted[i];
      const last = merged[merged.length - 1];

      if (current.start <= last.end) {
        // Fusionner
        last.end = Math.max(last.end, current.end);
      } else {
        merged.push(current);
      }
    }

    return merged;
  }

  /**
   * Extraire un segment en stream copy (pas de ré-encodage)
   */
  private async extractSegmentStreamCopy(segment: ExportSegment): Promise<string> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    const sourceFilename = await this.ensureSourceFile(segment.sourceClip);
    const outputFilename = `segment_${this.tempFileSequence++}.mp4`;

    // Alignement STRICT sur la grille de frame pour éviter les erreurs d'arrondi
    const fps = this.config.fps || 30;
    const frameDuration = 1 / fps;

    // Check if this segment goes to the end of the source file
    // If so, we SHOULD NOT use -t (duration) to avoid cutting the last frame due to rounding errors
    const sourceDuration = segment.sourceClip.originalDuration || 0;
    const isAtEndOfSource = sourceDuration > 0 && (segment.startInSource + segment.duration) >= (sourceDuration - frameDuration);

    const alignedStart = Math.round(segment.startInSource / frameDuration) * frameDuration;
    const alignedDuration = Math.round(segment.duration / frameDuration) * frameDuration;

    const ffmpegArgs = [
      '-ss', alignedStart.toFixed(6),
      '-i', sourceFilename
    ];

    if (!isAtEndOfSource) {
      ffmpegArgs.push('-t', alignedDuration.toFixed(6));
    } else {
      console.log(`[PreRenderMuxing] Optimization: Run to End (omitting -t) for ${outputFilename}`);
    }

    ffmpegArgs.push(
      '-c', 'copy',
      '-avoid_negative_ts', 'make_zero',
      outputFilename
    );

    await this.execFFmpeg(ffmpegArgs, `extract segment stream copy`);

    console.log(`[PreRenderMuxing] Segment stream copy: ${outputFilename} (${alignedDuration.toFixed(4)}s) ${isAtEndOfSource ? '[EOF]' : ''}`);

    return outputFilename;
  }

  /**
   * Pré-rendre un segment avec ses effets (Capture temps réel via MediaRecorder)
   */
  private async preRenderSegment(
    segment: ExportSegment,
    allClips: Clip[],
    tracks: Track[],
    activeSubtitleLanguage: string,
    subtitleStyle: SubtitleStyle
  ): Promise<string> {
    const { sourceClip, effects, startInSource, duration, timelineStart } = segment;

    console.log(`[PreRenderMuxing-RT] Start real-time render for segment: ${duration}s`);

    // 1. Setup Canvas (DOM element for captureStream)
    const resolution = ASPECT_RATIOS[this.config.aspectRatio];
    // NOTE: On utilise un canvas DOM standard car OffscreenCanvas.captureStream n'est pas standard partout
    // et on est dans un contexte DOM (pas Worker).
    const canvas = window.document.createElement('canvas');
    canvas.width = resolution.width;
    canvas.height = resolution.height;
    const ctx = canvas.getContext('2d', { alpha: true, desynchronized: false }); // Sync drawing preferred for quality

    if (!ctx) throw new Error('Impossible de créer le contexte canvas');

    // 2. Identify and Setup Video Sources
    const overlappingVideos = allClips.filter(c => {
      const track = tracks.find(t => t.id === c.trackId);
      if (!track?.isVisible) return false;
      if (c.type !== 'video' && c.clipRole !== 'intro' && c.clipRole !== 'outro') return false;
      return c.start < timelineStart + duration && c.start + c.duration > timelineStart;
    });

    const loadVideo = async (url: string): Promise<HTMLVideoElement> => {
      const v = window.document.createElement('video');
      v.crossOrigin = 'anonymous';
      v.muted = true;
      v.preload = 'auto';
      v.src = url;
      await new Promise<void>((resolve, reject) => {
        v.onloadedmetadata = () => resolve();
        v.onerror = () => reject(new Error(`Failed to load video ${url}`));
      });
      return v;
    };

    const videoElements = new Map<string, HTMLVideoElement>();
    for (const clip of overlappingVideos) {
      const sourceUrl = getClipExportSourceUrl(clip);
      if (sourceUrl) {
        const v = await loadVideo(sourceUrl);
        videoElements.set(clip.id, v);
      }
    }

    // Identify specific videos for legacy effects logic if needed
    const sourceVideo = videoElements.get(sourceClip.id);
    const introVideo = effects.intro ? videoElements.get(effects.intro.id) : null;
    const outroVideo = effects.outro ? videoElements.get(effects.outro.id) : null;
    const splitVideo = effects.split ? videoElements.get(effects.split.id) : null;

    // 3. Setup Overlays (Images/Text)
    const overlayClips = [...effects.texts, ...effects.images];
    const frameRenderer = new FrameRenderer(resolution.width, resolution.height);
    await frameRenderer.preloadMedia(overlayClips.filter(c => c.type === 'image'));

    // --- NEW: Live Preview Overlay Setup ---
    const { container, status } = this.createPreviewOverlay(canvas);
    // --------------------------------------

    // 4. Setup Image Mask
    let maskImage: HTMLImageElement | null = null;
    if (effects.imageMask) {
      maskImage = new Image();
      maskImage.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        maskImage!.onload = () => resolve();
        maskImage!.onerror = () => {
          console.warn('[PreRenderMuxing] Failed to load Image Mask:', effects.imageMask);
          maskImage = null;
          resolve(); // Resolve anyway so export continues
        };
        maskImage!.src = effects.imageMask!;
      });
    }

    // 5. Setup MediaRecorder
    // 30 FPS constant pour le stream
    const stream = canvas.captureStream(this.config.fps);
    const mimeType = 'video/webm;codecs=vp9'; // Chrome/Firefox standard

    // Check support
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      console.warn(`[PreRenderMuxing-RT] ${mimeType} not supported, falling back to video/webm`);
    }

    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: MediaRecorder.isTypeSupported(mimeType) ? mimeType : 'video/webm',
      videoBitsPerSecond: this.config.videoBitrate
    });

    const chunks: Blob[] = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    // 5. Real-time Render Loop
    return new Promise<string>(async (resolve, reject) => {
      // Setup start times and PRE-SEEK for all videos
      for (const [clipId, video] of videoElements.entries()) {
        const clip = overlappingVideos.find(c => c.id === clipId);
        if (clip) {
          video.currentTime = Math.max(0, (timelineStart - clip.start) + (clip.trimStart || 0));
        }
      }

      const waitForAllReady = async () => {
        const videos = Array.from(videoElements.values());
        await Promise.all(videos.map(v => {
          return new Promise<void>(r => {
            if (v.readyState >= 2) r();
            else v.onseeked = () => r();
          });
        }));
      };

      await waitForAllReady();

      // Start recording
      mediaRecorder.start();

      // Start playback simultaneously
      const videosToPlay = Array.from(videoElements.values());
      await Promise.all(videosToPlay.map(v => v.play().catch(console.error)));

      const startTime = performance.now();
      let animationFrameId: number;

      const loop = async () => {
        if (this.aborted) {
          mediaRecorder.stop();
          videosToPlay.forEach(v => v.pause());
          if (document.body.contains(container)) document.body.removeChild(container);
          reject(new Error('Export aborted'));
          return;
        }

        const now = performance.now();
        const elapsed = (now - startTime) / 1000;
        status.textContent = `Recording LIVE VFX: ${elapsed.toFixed(1)}s / ${duration.toFixed(1)}s`;

        if (elapsed >= duration) {
          videosToPlay.forEach(v => v.pause());
          mediaRecorder.stop();
          cancelAnimationFrame(animationFrameId);
          return;
        }

        const timelineTime = timelineStart + elapsed;
        ctx.clearRect(0, 0, resolution.width, resolution.height);

        // --- DRAWING ORDER (Full Multi-layer Composition) ---
        const activeClipsAtTime = allClips.filter(c => {
          const track = tracks.find(t => t.id === c.trackId);
          // Only render visible clips that are NOT subtitles (handled separately if needed)
          return track?.isVisible && timelineTime >= c.start && timelineTime < c.start + c.duration;
        });

        // Loop through tracks from bottom to top (idx: length-1 -> 0)
        for (let i = tracks.length - 1; i >= 0; i--) {
          const track = tracks[i];
          if (!track.isVisible) continue;

          const clipsOnTrack = activeClipsAtTime.filter(c => c.trackId === track.id);

          for (const clip of clipsOnTrack) {
            // Apply global zoom if this is a "content" track (not overlay track t1)
            const isContentTrack = track.id !== 't1';

            ctx.save();
            if (isContentTrack && effects.zoom) {
              this.applyZoomTransform(ctx, effects.zoom, timelineTime, resolution);
            }

            if (clip.type === 'video' || clip.clipRole === 'intro' || clip.clipRole === 'outro') {
              const video = videoElements.get(clip.id);
              if (video) {
                // Split screen logic takeover
                if (track.id === 's1' && effects.split && sourceVideo) {
                  await this.renderSplitScreen(ctx, sourceVideo, effects.split, timelineTime, resolution);
                } else {
                  this.renderVideoClipRealTime(ctx, clip, video, timelineTime, resolution, maskImage);
                }
              }
            } else if (clip.type === 'text' || clip.type === 'image') {
              await frameRenderer.renderClip(ctx, clip, timelineTime, tracks, this.config.scaleMode || 'fill');
            }

            ctx.restore();
          }
        }

        // Render subtitles last (if requested to burn)
        if (this.config.burnSubtitles) {
          (frameRenderer as any).renderSubtitles(
            ctx,
            activeClipsAtTime,
            timelineTime,
            activeSubtitleLanguage,
            subtitleStyle
          );
        }

        animationFrameId = requestAnimationFrame(loop);
      };

      // Handle Stop
      mediaRecorder.onstop = async () => {
        const fullBlob = new Blob(chunks, { type: 'video/webm' });
        const blobUrl = URL.createObjectURL(fullBlob);

        // Cleanup overlay immediately (Automated flow)
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }

        // Report preview for UI list (vignette)
        if (this.progressCallback) {
          this.progressCallback({
            phase: 'encoding',
            progress: this.calculateGlobalProgress(100),
            message: 'Segment terminé',
            segmentPreview: {
              id: `seg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              name: `Segment FX (${duration.toFixed(1)}s)`,
              blobUrl: blobUrl,
              duration: duration
            }
          } as any);
        }

        const outputFilename = `segment_${this.tempFileSequence++}.webm`;
        const data = new Uint8Array(await fullBlob.arrayBuffer());
        if (this.ffmpeg) await this.ffmpeg.writeFile(outputFilename, data);

        frameRenderer.cleanup();
        resolve(outputFilename);
      };

      mediaRecorder.onerror = (e) => reject((e as any).error);
      loop();
    });
  }

  /**
   * Helper to create a DOM overlay for live preview (integrated PIP monitor)
   */
  private createPreviewOverlay(canvas: HTMLCanvasElement): { container: HTMLElement, status: HTMLElement } {
    const container = document.createElement('div');
    container.id = 'vfx-live-monitor';
    container.style.position = 'fixed';
    container.style.bottom = '20px';
    container.style.right = '320px'; // To the left of the right menu
    container.style.width = '240px';
    container.style.height = '135px'; // 16:9
    container.style.backgroundColor = '#1a1a1a';
    container.style.border = '2px solid #444';
    container.style.borderRadius = '8px';
    container.style.overflow = 'hidden';
    container.style.boxShadow = '0 8px 32px rgba(0,0,0,0.5)';
    container.style.zIndex = '9999';
    container.style.display = 'flex';
    container.style.flexDirection = 'column';
    container.style.transition = 'all 0.3s ease';
    container.style.pointerEvents = 'none'; // Don't block clicks

    // Header / Status
    const status = document.createElement('div');
    status.style.padding = '4px 8px';
    status.style.fontSize = '10px';
    status.style.fontWeight = 'bold';
    status.style.color = '#fff';
    status.style.backgroundColor = 'rgba(0,0,0,0.7)';
    status.style.fontFamily = 'monospace';
    status.textContent = "LIVE VFX CAPTURE...";

    // Canvas wrapping for scaling
    const canvasWrap = document.createElement('div');
    canvasWrap.style.flex = '1';
    canvasWrap.style.position = 'relative';
    canvasWrap.style.overflow = 'hidden';

    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.objectFit = 'contain';

    canvasWrap.appendChild(canvas);
    container.appendChild(status);
    container.appendChild(canvasWrap);

    document.body.appendChild(container);

    return { container, status };
  }

  /**
   * Appliquer la transformation de zoom au contexte
   */
  private applyZoomTransform(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    zoomClip: Clip,
    currentTime: number,
    resolution: { width: number; height: number }
  ): void {
    if (!zoomClip.transform || !zoomClip.endTransform) return;

    const { transform: defaultTransform, endTransform: targetTransform, duration, start } = zoomClip;
    const timeIntoClip = currentTime - start;
    const animationTime = 1.0;
    const inDuration = Math.min(animationTime, duration / 2);
    const outStartTime = duration - inDuration;

    let progress = 0;
    if (timeIntoClip < inDuration) {
      progress = this.easeInOutCubic(timeIntoClip / inDuration);
    } else if (timeIntoClip > outStartTime && outStartTime > inDuration) {
      progress = 1 - this.easeInOutCubic((timeIntoClip - outStartTime) / inDuration);
    } else if (timeIntoClip >= inDuration && timeIntoClip <= outStartTime) {
      progress = 1;
    }

    const camX = defaultTransform.x + (targetTransform.x - defaultTransform.x) * progress;
    const camY = defaultTransform.y + (targetTransform.y - defaultTransform.y) * progress;
    const camScale = defaultTransform.scale + (targetTransform.scale - defaultTransform.scale) * progress;
    const camRot = defaultTransform.rotation + (targetTransform.rotation - defaultTransform.rotation) * progress;

    ctx.translate(resolution.width / 2, resolution.height / 2);
    if (camScale !== 0) ctx.scale(1 / camScale, 1 / camScale);
    ctx.rotate(camRot * Math.PI / 180);
    ctx.translate(-resolution.width / 2 - camX, -resolution.height / 2 - camY);
  }

  /**
   * Render split screen
   */
  private async renderSplitScreen(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    leftVideo: HTMLVideoElement,
    splitClip: Clip,
    currentTime: number,
    resolution: { width: number; height: number }
  ): Promise<void> {
    const animationDuration = 1.0;
    const timeIntoClip = currentTime - splitClip.start;
    const timeBeforeEnd = (splitClip.start + splitClip.duration) - currentTime;
    let progress = 1.0;

    if (timeIntoClip < animationDuration) {
      progress = timeIntoClip / animationDuration;
    } else if (timeBeforeEnd < animationDuration) {
      progress = timeBeforeEnd / animationDuration;
    }
    const finalProgress = this.easeInOutCubic(progress);

    // Charger la vidéo du split
    let splitVideo: HTMLVideoElement | null = null;
    if (splitClip.url) {
      splitVideo = window.document.createElement('video');
      splitVideo.crossOrigin = 'anonymous';
      splitVideo.muted = true;
      splitVideo.preload = 'auto';

      await new Promise<void>((resolve, reject) => {
        splitVideo!.addEventListener('loadedmetadata', () => resolve(), { once: true });
        splitVideo!.addEventListener('error', () => reject(new Error('Erreur chargement split')), { once: true });
        splitVideo!.src = splitClip.url!;
        splitVideo!.load();
      });

      const splitTime = (currentTime - splitClip.start) + (splitClip.trimStart || 0);
      splitVideo.currentTime = splitTime;
      await this.waitForVideoReady(splitVideo);
    }

    // Render left side
    ctx.save();
    const leftScale = 1.0 - (0.05 * finalProgress);
    const leftTranslateX = (-resolution.width / 4) * finalProgress;
    ctx.translate(resolution.width / 2, resolution.height / 2);
    ctx.scale(leftScale, leftScale);
    ctx.translate(-resolution.width / 2 + leftTranslateX, -resolution.height / 2);
    this.drawVideoToCanvas(ctx, leftVideo, resolution);
    ctx.restore();

    // Render right side
    if (splitVideo) {
      ctx.save();
      const panelLeadingEdgeX = resolution.width - (resolution.width / 2) * finalProgress;
      const rightClipPath = new Path2D();
      rightClipPath.rect(panelLeadingEdgeX, 0, resolution.width - panelLeadingEdgeX, resolution.height);
      ctx.clip(rightClipPath);
      const contentTranslationX = panelLeadingEdgeX - (resolution.width / 2);
      ctx.translate(contentTranslationX, 0);
      const rightScale = 1.0 - (0.05 * finalProgress);
      const rightTranslateX = (resolution.width / 4) * finalProgress;
      ctx.translate(resolution.width / 2, resolution.height / 2);
      ctx.scale(rightScale, rightScale);
      ctx.translate(-resolution.width / 2 + rightTranslateX, -resolution.height / 2);
      this.drawVideoToCanvas(ctx, splitVideo, resolution);
      ctx.restore();
    }
  }

  /**
   * Render video clip in real-time with opacity and transforms
   */
  private renderVideoClipRealTime(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    clip: Clip,
    video: HTMLVideoElement,
    currentTime: number,
    resolution: { width: number; height: number },
    maskImage?: HTMLImageElement | null
  ): void {
    const relativeTime = currentTime - clip.start;
    let opacity = clip.opacity ?? 1.0;

    // Apply fades
    if (clip.fadeInDuration && relativeTime < clip.fadeInDuration) {
      opacity *= relativeTime / clip.fadeInDuration;
    }
    const fadeOutStart = clip.duration - (clip.fadeOutDuration || 0);
    if (clip.fadeOutDuration && relativeTime > fadeOutStart) {
      opacity *= (clip.duration - relativeTime) / clip.fadeOutDuration;
    }

    if (opacity <= 0) return;

    ctx.save();
    ctx.globalAlpha = opacity;

    // Apply custom transform if present
    if (clip.transform) {
      ctx.translate(resolution.width / 2, resolution.height / 2);
      ctx.rotate((clip.transform.rotation * Math.PI) / 180);
      ctx.scale(clip.transform.scale, clip.transform.scale);
      ctx.translate(clip.transform.x, clip.transform.y);
      ctx.translate(-resolution.width / 2, -resolution.height / 2);
    }

    this.drawVideoToCanvas(ctx, video, resolution);

    // Draw Nano Banana Image Mask if present
    if (maskImage) {
      // Draw the image directly on top of the video since it covers exactly what was extracted
      this.fillCanvasWithImage(ctx, maskImage, resolution);
    }

    ctx.restore();
  }

  private fillCanvasWithImage(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    img: HTMLImageElement,
    resolution: { width: number; height: number }
  ): void {
    const imgAspect = img.width / img.height;
    const canvasAspect = resolution.width / resolution.height;

    let drawWidth, drawHeight;
    const shouldFill = this.config.scaleMode === 'fill' || !this.config.scaleMode;

    if (shouldFill) {
      if (imgAspect > canvasAspect) {
        drawHeight = resolution.height;
        drawWidth = drawHeight * imgAspect;
      } else {
        drawWidth = resolution.width;
        drawHeight = drawWidth / imgAspect;
      }
    } else {
      if (imgAspect > canvasAspect) {
        drawWidth = resolution.width;
        drawHeight = drawWidth / imgAspect;
      } else {
        drawHeight = resolution.height;
        drawWidth = drawHeight * imgAspect;
      }
    }

    const x = (resolution.width - drawWidth) / 2;
    const y = (resolution.height - drawHeight) / 2;

    ctx.drawImage(img, x, y, drawWidth, drawHeight);
  }

  /**
   * Dessiner une vidéo sur le canvas
   */
  private drawVideoToCanvas(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    video: HTMLVideoElement,
    resolution: { width: number; height: number }
  ): void {
    const mediaWidth = video.videoWidth;
    const mediaHeight = video.videoHeight;

    if (mediaWidth === 0 || mediaHeight === 0) return;

    const mediaAspect = mediaWidth / mediaHeight;
    const canvasAspect = resolution.width / resolution.height;

    let drawWidth, drawHeight;
    const shouldFill = this.config.scaleMode === 'fill' || !this.config.scaleMode;

    if (shouldFill) {
      // FILL (Cover) mode
      if (mediaAspect > canvasAspect) {
        drawHeight = resolution.height;
        drawWidth = drawHeight * mediaAspect;
      } else {
        drawWidth = resolution.width;
        drawHeight = drawWidth / mediaAspect;
      }
    } else {
      // FIT (Contain) mode
      if (mediaAspect > canvasAspect) {
        // Media is wider - fit to width
        drawWidth = resolution.width;
        drawHeight = drawWidth / mediaAspect;
      } else {
        // Media is taller - fit to height
        drawHeight = resolution.height;
        drawWidth = drawHeight * mediaAspect;
      }
    }

    const x = (resolution.width - drawWidth) / 2;
    const y = (resolution.height - drawHeight) / 2;

    ctx.drawImage(video, x, y, drawWidth, drawHeight);
  }

  /**
   * Attendre que la vidéo soit prête
   */
  private async waitForVideoReady(video: HTMLVideoElement): Promise<void> {
    if (video.readyState >= 2) return;

    await new Promise<void>((resolve) => {
      const timeout = setTimeout(() => resolve(), 5000);

      video.addEventListener('seeked', () => {
        clearTimeout(timeout);
        resolve();
      }, { once: true });

      if (video.readyState >= 2) {
        clearTimeout(timeout);
        resolve();
      }
    });
  }

  /**
   * Muxer tous les segments ensemble
   */
  private async muxAllSegments(
    segments: ExportSegment[],
    clips: Clip[],
    activeSubtitleLanguage: string
  ): Promise<Blob> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    // Créer le manifest concat avec directives inpoint/outpoint
    const manifestEntries: string[] = [];

    for (const segment of segments) {
      if (segment.type === 'prerender' && segment.outputFilename) {
        // Segment pré-rendu : fichier temporaire complet
        manifestEntries.push(`file '${segment.outputFilename}'`, '');
      } else if (segment.type === 'streamcopy') {
        // Segment source : référence au fichier source avec inpoint/outpoint
        const sourceFilename = await this.ensureSourceFile(segment.sourceClip);

        // inpoint = startInSource (inclusif)
        // outpoint = startInSource + duration (exclusif)
        const inpoint = segment.startInSource;
        const outpoint = segment.startInSource + segment.duration;

        manifestEntries.push(
          `file '${sourceFilename}'`,
          `inpoint ${inpoint.toFixed(6)}`,
          `outpoint ${outpoint.toFixed(6)}`,
          ''
        );
      }
    }

    console.log('[PreRenderMuxing] Manifest concat généré:', manifestEntries.join('\n'));

    // Identifier les segments WebM pré-rendus
    const webmSegments = segments.filter(s => s.type === 'prerender' && s.outputFilename?.endsWith('.webm'));
    const hasWebmPrerender = webmSegments.length > 0;

    // OPTIMISATION CLEF : Convertir les segments WebM en MP4 d'abord (petits fichiers)
    // Puis faire un concat 100% MP4 avec -c copy (ultra rapide)
    if (hasWebmPrerender) {
      // CRITIQUE: Terminer FFmpeg et recharger une instance fraîche pour libérer TOUTE la mémoire
      // La simple suppression de fichiers ne libère pas la mémoire WebAssembly accumulée
      console.log('[PreRenderMuxing] Terminaison FFmpeg pour libérer la mémoire...');

      // Sauvegarder les fichiers WebM en mémoire JavaScript AVANT de terminer FFmpeg
      const webmBlobs: Map<string, Blob> = new Map();
      for (const segment of webmSegments) {
        if (segment.outputFilename) {
          try {
            const data = await this.ffmpeg!.readFile(segment.outputFilename);
            webmBlobs.set(segment.outputFilename, new Blob([data as any], { type: 'video/webm' }));
            console.log(`[PreRenderMuxing] WebM sauvegardé: ${segment.outputFilename} (${(data as Uint8Array).length} bytes)`);
          } catch (e) {
            console.warn(`[PreRenderMuxing] Impossible de sauvegarder ${segment.outputFilename}:`, e);
          }
        }
      }

      // Terminer l'instance FFmpeg actuelle
      await this.ffmpeg!.terminate();
      FFmpegService.markTerminated();
      this.ffmpeg = null;
      this.isFFmpegLoaded = false;
      this.sourceCache.clear();

      // Recharger FFmpeg avec une instance fraîche (mémoire vide)
      console.log('[PreRenderMuxing] Rechargement FFmpeg (instance fraîche)...');
      await this.loadFFmpeg();

      // Réécrire les fichiers WebM dans la nouvelle instance
      for (const [filename, blob] of webmBlobs) {
        const data = new Uint8Array(await blob.arrayBuffer());
        await this.ffmpeg!.writeFile(filename, data);
        console.log(`[PreRenderMuxing] WebM restauré: ${filename}`);
      }
      webmBlobs.clear(); // Libérer la mémoire JavaScript

      this.updateProgress({
        phase: 'muxing',
        progress: 78,
        message: `Conversion des ${webmSegments.length} segments pré-rendus en MP4...`
      });

      // Convertir chaque segment WebM en MP4
      for (let i = 0; i < webmSegments.length; i++) {
        const segment = webmSegments[i];
        if (!segment.outputFilename) continue;

        const webmFile = segment.outputFilename;
        const mp4File = webmFile.replace('.webm', '.mp4');

        console.log(`[PreRenderMuxing] Conversion ${webmFile} -> ${mp4File} (${i + 1}/${webmSegments.length})`);



        // Update individual segment progress (mark as processing)
        const segmentIndex = segments.findIndex(s => s.outputFilename === webmFile);
        if (segmentIndex !== -1 && this.segmentDetails[segmentIndex]) {
          this.segmentDetails[segmentIndex].progress = 0;
          this.segmentDetails[segmentIndex].status = 'converting';

          // Set context for the log parser to track real-time progress
          this.currentSegmentIndex = segmentIndex;
          this.currentSegmentTargetDuration = segment.duration;

          this.updateProgress({
            phase: 'muxing',
            progress: 78 + Math.floor((i / webmSegments.length) * 5),
            message: `Conversion segment ${i + 1}/${webmSegments.length}...`
          });
        }

        await this.execFFmpeg([
          '-i', webmFile,
          '-f', 'lavfi', '-i', 'anullsrc=channel_layout=stereo:sample_rate=44100', // Générer silence pour maintenir la structure du flux
          '-c:v', 'libx264',
          '-preset', 'ultrafast',
          '-crf', '18',
          '-r', `${this.config.fps}`,
          // REMOVED: '-video_track_timescale', '30000', // CAUSE DU RALENTI SUR 60FPS
          '-c:a', 'aac',
          '-b:a', '128k',
          '-shortest', // Couper le silence à la fin de la vidéo
          mp4File
        ], `convert ${webmFile} to MP4`);

        // Clear context and mark done
        this.currentSegmentIndex = null;
        this.currentSegmentTargetDuration = null;
        if (segmentIndex !== -1 && this.segmentDetails[segmentIndex]) {
          this.segmentDetails[segmentIndex].progress = 100;
        }

        // Mettre à jour le nom du fichier dans le segment
        segment.outputFilename = mp4File;

        // Supprimer le fichier WebM immédiatement pour libérer la mémoire
        await this.safeDelete(webmFile);
      }

      // --- PAUSE POUR VERIFICATION UTILISATEUR (DEBUG) ---
      // Demander confirmation avant le concat final pour laisser l'utilisateur vérifier les segments
      if (this.config.strategy === 'prerender') { // Uniquement en mode explicite
        const proceed = window.confirm(
          `[BETA DEBUG] Segments générés (MP4).\n\n` +
          `Total segments: ${segments.length}\n` +
          `Prêts à être concaténés.\n\n` +
          `Voulez-vous procéder au CONCAT final ?\n` +
          `(Annuler stoppera l'export ici pour inspection)`
        );

        if (!proceed) {
          throw new Error('Export annulé par l\'utilisateur avant concat');
        }
      }

      // Recharger les fichiers source pour le concat
      console.log('[PreRenderMuxing] Rechargement des sources pour le concat...');
      for (const segment of segments) {
        if (segment.type === 'streamcopy' && getClipExportSourceUrl(segment.sourceClip)) {
          await this.ensureSourceFile(segment.sourceClip);
        }
      }

      // Reconstruire le manifest avec les nouveaux noms de fichiers MP4
      manifestEntries.length = 0;
      for (const segment of segments) {
        if (segment.type === 'prerender' && segment.outputFilename) {
          manifestEntries.push(`file '${segment.outputFilename}'`, '');
        } else if (segment.type === 'streamcopy') {
          const sourceFilename = this.sourceCache.get(getClipExportSourceUrl(segment.sourceClip) || '');
          if (sourceFilename) {
            const inpoint = segment.startInSource;
            const outpoint = segment.startInSource + segment.duration;
            manifestEntries.push(
              `file '${sourceFilename}'`,
              `inpoint ${inpoint.toFixed(6)}`,
              `outpoint ${outpoint.toFixed(6)}`,
              ''
            );
          }
        }
      }

      console.log('[PreRenderMuxing] Manifest MP4 unifié:', manifestEntries.join('\n'));

      await this.ffmpeg.writeFile(
        'concat_final.txt',
        new TextEncoder().encode(manifestEntries.join('\n'))
      );
    } else {
      await this.ffmpeg.writeFile(
        'concat_final.txt',
        new TextEncoder().encode(manifestEntries.join('\n'))
      );
    }

    // Maintenant tout est en MP4, on peut faire l'assemblage final
    const outputFile = 'output.mp4';

    this.updateProgress({
      phase: 'muxing',
      progress: 85,
      message: 'Assemblage final (Copie ultra-rapide) ⚡'
    });

    // Stream copy - Smart Rendering (pas de ré-encodage complet)
    // NOTE: Si le point de coupe n'est pas sur une Keyframe, il peut y avoir un court gel.
    // C'est le compromis pour la vitesse "instantanée".
    try {
      await this.execFFmpeg([
        '-fflags', '+genpts', // CRUCIAL : Régénérer les timestamps pour éviter les fichiers cassés
        '-f', 'concat',
        '-safe', '0',
        '-i', 'concat_final.txt',
        '-c', 'copy',
        '-avoid_negative_ts', 'make_zero', // CRUCIAL : Assurer que la vidéo commence à 0
        '-bsf:a', 'aac_adtstoasc',
        '-y',
        outputFile
      ], 'concat final ({c copy})');
    } catch (error) {
      if (error instanceof FFmpegOutOfMemoryError) throw error;

      console.warn('Concat copy failed, retrying without bsf...');

      // Retry without bsf
      try {
        await this.execFFmpeg([
          '-fflags', '+genpts',
          '-f', 'concat',
          '-safe', '0',
          '-i', 'concat_final.txt',
          '-c', 'copy',
          '-avoid_negative_ts', 'make_zero',
          '-y',
          outputFile
        ], 'concat final (copy simple)');
      } catch (e) {
        if (e instanceof FFmpegOutOfMemoryError) throw e;

        // Fallback re-encode ONLY if copy fails completely
        console.warn('Concat copy completely failed, falling back to safe re-encode');
        await this.execFFmpeg([
          '-f', 'concat',
          '-safe', '0',
          '-i', 'concat_final.txt',
          '-c:v', 'libx264',
          '-preset', 'ultrafast',
          '-c:a', 'aac',
          '-y',
          outputFile
        ], 'concat final (re-encode fallback)');
      }
    }

    console.log('[PreRenderMuxing] ✅ Concat ultra-rapide terminé');

    this.updateProgress({
      phase: 'muxing',
      progress: 95,
      message: 'Finalisation...'
    });

    const outputData = await this.ffmpeg.readFile(outputFile);
    const outputBlob = new Blob([outputData as any], {
      type: 'video/mp4'
    });

    // Ajouter sous-titres si présents
    const hasSubtitles = clips.some(clip =>
      clip.subtitles &&
      clip.subtitles[activeSubtitleLanguage] &&
      clip.subtitles[activeSubtitleLanguage].length > 0
    );

    if (hasSubtitles) {
      // Si Burn-in est désactivé, on ajoute les soft subtitles
      if (!this.config.burnSubtitles) {
        console.log('[PreRenderMuxing] Ajout des soft subtitles (mov_text)...');
        return await this.addSubtitles(outputBlob, clips, activeSubtitleLanguage);
      }
    }

    console.log('[PreRenderMuxing] Export terminé:', outputBlob.size, 'bytes');

    return outputBlob;
  }

  /**
   * Ajouter les sous-titres
   */
  private async addSubtitles(
    videoBlob: Blob,
    clips: Clip[],
    activeSubtitleLanguage: string
  ): Promise<Blob> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    const allSubtitles = clips
      .filter(clip => clip.subtitles && clip.subtitles[activeSubtitleLanguage])
      .flatMap(clip => clip.subtitles![activeSubtitleLanguage])
      .sort((a, b) => a.start - b.start);

    if (allSubtitles.length === 0) return videoBlob;

    const srtContent = this.generateSRT(allSubtitles);

    await this.ffmpeg.writeFile('video_for_subs.mp4', new Uint8Array(await videoBlob.arrayBuffer()));
    await this.ffmpeg.writeFile('subtitles.srt', new TextEncoder().encode(srtContent));

    await this.execFFmpeg([
      '-i', 'video_for_subs.mp4',
      '-i', 'subtitles.srt',
      '-c', 'copy',
      '-c:s', 'mov_text',
      '-metadata:s:s:0', `language=${activeSubtitleLanguage}`,
      'output_with_subs.mp4'
    ], 'mux subtitles');

    const outputData = await this.ffmpeg.readFile('output_with_subs.mp4');
    return new Blob([outputData as any], { type: 'video/mp4' });
  }

  private generateSRT(subtitles: any[]): string {
    return subtitles.map((sub, index) => {
      const startTime = this.formatSRTTime(sub.start);
      const endTime = this.formatSRTTime(sub.end);
      return `${index + 1}\n${startTime} --> ${endTime}\n${sub.text}\n`;
    }).join('\n');
  }

  private formatSRTTime(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    const milliseconds = Math.floor((seconds % 1) * 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')},${milliseconds.toString().padStart(3, '0')}`;
  }

  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // FFmpeg helpers
  private async loadFFmpeg(): Promise<void> {
    if (this.isFFmpegLoaded && this.ffmpeg) return;

    console.log('[PreRenderMuxing] Chargement FFmpeg...');
    this.ffmpeg = await FFmpegService.getInstance();
    this.ffmpegLogs = [];

    this.ffmpeg.on('log', ({ message }) => {
      // console.log('[FFmpeg]', message); // Trop verbeux
      this.recordFFmpegLog(message);

      // Parse progress if valid segment context exists
      if (this.currentSegmentTargetDuration && this.currentSegmentIndex !== null && this.segmentDetails[this.currentSegmentIndex]) {
        const timeMatch = message.match(/time=(\d{2}):(\d{2}):(\d{2}\.\d+)/);
        if (timeMatch) {
          const hours = parseFloat(timeMatch[1]);
          const minutes = parseFloat(timeMatch[2]);
          const seconds = parseFloat(timeMatch[3]);
          const totalSeconds = hours * 3600 + minutes * 60 + seconds;

          const percent = Math.min(99, Math.round((totalSeconds / this.currentSegmentTargetDuration) * 100));

          // Throttle updates (max 10fps)
          const now = Date.now();
          if (percent !== this.segmentDetails[this.currentSegmentIndex].progress && (now - this.lastProgressUpdate > 100)) {
            this.segmentDetails[this.currentSegmentIndex].progress = percent;
            this.lastProgressUpdate = now;
            this.updateProgress({
              phase: 'muxing', // Re-use current phase or generic
              progress: -1, // -1 signals "don't update global bar deeply if not needed" or handled by internal logic. 
              // Actually, let's just trigger updates. The main progress bar might jitter if we don't control it. 
              // But "details" update is what we want.
              message: `Conversion segment ${this.currentSegmentIndex + 1}... ${percent}%`
            });
          }
        }
      }
    });

    this.isFFmpegLoaded = true;
    console.log('[PreRenderMuxing] FFmpeg prêt ✓');
  }

  private async ensureSourceFile(source: Clip | string): Promise<string> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    const url = typeof source === 'string' ? source : getClipExportSourceUrl(source);
    if (!url) throw new Error('Source vidéo introuvable');

    const cached = this.sourceCache.get(url);
    if (cached) return cached;

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Impossible de télécharger la source vidéo (${response.status})`);
    }

    const sourceBlob = await response.blob();
    const sourceFilename = `source_${this.sourceSequence++}.mp4`;
    await this.ffmpeg.writeFile(sourceFilename, await fetchFile(sourceBlob));

    this.sourceCache.set(url, sourceFilename);
    return sourceFilename;
  }

  private async execFFmpeg(args: string[], context: string): Promise<void> {
    if (!this.ffmpeg) throw new Error('FFmpeg non initialisé');

    this.resetFFmpegLogBuffer();

    try {
      await this.ffmpeg.exec(args);
    } catch (error) {
      if (isLikelyFFmpegOutOfMemoryError(error, this.ffmpegLogs)) {
        throw new FFmpegOutOfMemoryError(
          `FFmpeg est à court de mémoire pendant ${context}`,
          { cause: error, logs: [...this.ffmpegLogs] }
        );
      }
      throw error instanceof Error ? error : new Error(String(error));
    }
  }

  private async safeDelete(filename: string): Promise<void> {
    if (!this.ffmpeg || !filename) return;
    try {
      await this.ffmpeg.deleteFile(filename);
    } catch (e) {
      // Ignore
    }
  }

  private resetFFmpegLogBuffer(): void {
    this.ffmpegLogs = [];
  }

  private recordFFmpegLog(message: string): void {
    this.ffmpegLogs.push(message);
    if (this.ffmpegLogs.length > 200) {
      this.ffmpegLogs.shift();
    }
  }

  private async cleanup(): Promise<void> {
    if (this.ffmpeg && this.isFFmpegLoaded) {
      for (const filename of this.sourceCache.values()) {
        await this.safeDelete(filename);
      }
      this.sourceCache.clear();

      for (let i = 0; i < this.tempFileSequence; i++) {
        await this.safeDelete(`segment_${i}.mp4`);
        await this.safeDelete(`segment_${i}.webm`);
      }

      await this.safeDelete('concat_final.txt');
      await this.safeDelete('output.mp4');
      await this.safeDelete('output.webm');
      await this.safeDelete('video_only_temp.mp4');
      await this.safeDelete('video_only_temp.webm');
      await this.safeDelete('video_for_subs.mp4');
      await this.safeDelete('subtitles.srt');
      await this.safeDelete('output_with_subs.mp4');

      await this.ffmpeg.terminate();
      FFmpegService.markTerminated(); // Notifier le service que l'instance est terminée
      this.ffmpeg = null;
      this.isFFmpegLoaded = false;
      this.resetFFmpegLogBuffer();
    }
  }

  private updateProgress(progress: ExportProgress): void {
    if (this.aborted) return;

    // Injecter les détails si disponibles et non présents dans l'update actuel
    if (this.segmentDetails.length > 0 && !progress.details) {
      progress.details = this.segmentDetails;
    }

    this.progressCallback(progress);
  }
}
